import { useCallback } from 'react'
import { useRouter } from 'next/router'
import { useAnalytics } from '../../context/Analytics'
import useNeuroId from '../useNeuroId'
import type {
  BankAccountTypeV2,
  PrequalAndAppSubmissionBody,
} from '../../types/prequalAndAppSubmission.types'
import {
  BankAccountType,
  EmploymentStatus,
  HomeOwnership,
  NidPageIdentifier,
  type LegalDocument,
} from '../../types'
import type { Campaign } from '../../domain/campaign'
import { getUtmParams } from '../../lib/utils'
import { formatDateOfBirth, formatPhoneNumber, formatUsdAmount } from '../../utils/formatters'
import type { FormValues } from '../../form/schema'
import uriUtils from '../../lib/uriUtils'

export const translateBankAccount = (bankAccount: BankAccountType): BankAccountTypeV2 => {
  switch (bankAccount) {
    case 'SAVINGS':
      return 'SAVING'
    case 'BOTH':
      return 'CHECKING_AND_SAVING'
    default:
      return bankAccount as BankAccountTypeV2
  }
}

const useSanitizeFormValues = (
  campaignDetails: Campaign,
  disclosuresDoc: LegalDocument,
  offerCagDoc: LegalDocument
) => {
  const { submit: submitNeuroId } = useNeuroId()
  const { sessionId } = useAnalytics()
  const { query } = useRouter()

  const { getInviteCode } = uriUtils()

  return useCallback(
    (formData: FormValues) => {
      const { firstName, middleInitial, lastName } = formData.names
      const { email } = formData.emails
      const {
        dateOfBirth,
        socialSecurityNumber,
        streetAddress,
        unit,
        city,
        state,
        zip,
        housing,
        payment,
        employmentStatus,
        totalAnnualIncome,
        accounts,
        cashAdvance,
        phoneNumber,
        communicationConsent,
        contactConsent,
        paperlessStatements,
        creditProtection,
      } = formData
      const utmParams = getUtmParams(query)
      const {
        subChannelContext,
        bundle: {
          offers: [
            {
              product: { productCode },
            },
          ],
        },
      } = campaignDetails

      submitNeuroId(NidPageIdentifier.ApplicationInfo)

      // NEXT_PUBLIC_NEUROID_APP_FLOW_JS_SCRIPT (i.e. //scripts.neuro-id.com/c/nid-misLanCA-test.js)
      // grabs the misLanCA and sends it to the backend
      // so it can pull values from neuroid
      const nid = process.env.NEXT_PUBLIC_NEUROID_APP_FLOW_JS_SCRIPT?.split('/') ?? []
      const nidForm = nid.length > 0 ? nid[nid.length - 1].slice(0, -3) : ''
      const counterOfferBundleId = campaignDetails.counterOfferBundle?.id

      const body: PrequalAndAppSubmissionBody = {
        applicationType: 'APPLICATION',
        source: 'DIRECT_APPLICATION',
        applicants: [
          {
            applicantType: 'PRIMARY',
            firstName: firstName.trim(),
            middleName: middleInitial,
            lastName: lastName.trim(),
            ssn: socialSecurityNumber.replace(/-/g, ''),
            dateOfBirth: formatDateOfBirth(dateOfBirth),
            contactDetails: {
              emailAddress: email,
              phoneNumber: formatPhoneNumber(phoneNumber),
              address: {
                addressLineOne: streetAddress,
                addressLineTwo: unit,
                city,
                state,
                zip,
              },
              paperlessStatements,
            },
            financialDetails: {
              income: formatUsdAmount(totalAnnualIncome),
              monthlyHousingAmount: formatUsdAmount(payment),
              employmentStatus: employmentStatus.toUpperCase() as EmploymentStatus,
              bankAccount: translateBankAccount(accounts.toUpperCase() as BankAccountType),
              homeOwnership: housing.toUpperCase() as HomeOwnership,
              cashAdvances: cashAdvance === 'yes',
            },
          },
        ],
        productDetails: {
          featureFlags: {},
          inviteCode: getInviteCode(query),
          campaignId: campaignDetails.id,
          bundleId: campaignDetails.bundle.id,
          subChannelContextId: subChannelContext.id,
          productCode,
          ...(counterOfferBundleId ? { counterOfferBundleId } : {}),
        },
        addOnProducts: {
          creditProtectionProducts: [
            {
              name: 'COMPLETE',
              attributes: {
                customerSelection: creditProtection === 'yes' ? 'OPT_IN' : 'OPT_OUT',
              },
            },
          ],
        },
        utmParameters: utmParams,
        fraudIdentifiers: {
          directMailLookupUsed: query?.directmail_lookup_submission === 'true',
          neuroIdentityId: sessionId,
          neuroSiteId: nidForm,
          threatmetrixSessionId: sessionId,
          segmentId: sessionId,
          userAgent: window.navigator.userAgent,
          windowSize: window.innerWidth.toString(),
        },
        acceptedDisclosures: {
          electronicCommunication: communicationConsent && contactConsent,
          privacyPolicy: true,
          termsOfService: true,
          applicationAgreements: true,
          cardholderAgreements: true,
        },
        snapshotDocuments: [
          {
            type: 'APPLICATION_HTML',
            content: '',
          },
        ],
        legalDocuments: [disclosuresDoc, offerCagDoc],
      }

      return body
    },
    [campaignDetails, query, sessionId]
  )
}
export default useSanitizeFormValues
